import { DatePicker, Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getEventRecords } from "../../api/paymentsEvents.api";
import Button from "../../components/Button";
import DownloadButton from "../../components/DownloadButton";
import ErrorResponse from "../../components/ErrorResponse";
import Header from "../../components/layout/Header";
import Loader from "../../components/Loader";
import DatePickerTable from "../../components/payment/DatePickerTable";
import ScrollView from "../../components/payment/ScrollView";
import SelectCampus from "../../components/SelectCampus";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import { getCommonCodeLabel } from "../../lib/store";
import { IEventRecordItem, IGetEventRecords } from "../../models/paymentsEvents.model";
import { RootState } from "../../store/rootReducer";
import { useTranslation } from "react-i18next";
import TableEmpty from "../../components/common/TableEmpty";

const StatisticsEvent = () => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // PA0004 = 결제 채널
  const PA0004 = commonCodes.find(item => item.commonCode === 'PA0004')?.detailCode;    
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetEventRecords>();

  const [paymentMtdCode, setPaymentMtdCode] = useState<string>();
  const [paymentChannelCode, setPaymentChannelCode] = useState<string>();
  const [eventName, setEventName] = useState('');
  const [eventSrtDate, setEventSrtDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');

  useEffect(() => {
    if (serverDate) {
      const date = moment(serverDate).format('YYYYMMDD');
      setEventSrtDate(date);
      setEventEndDate(date);
    }
  }, [serverDate]);

  const onSearch = () => {
    if (!eventSrtDate || !eventEndDate) {
      return infoNotification(t('statistics.eventRefund.1'));
    }

    const req = {
      campusCode: currentCampus,
      paymentMtdCode,
      paymentChannelCode,
      eventName,
      eventSrtDate,
      eventEndDate,
      pageNo: 1,
      perPage: 999999999,
    };

    execute(getEventRecords(req));
  };

  const columns: TableColumnsType<IEventRecordItem> = [
    {
      title: t('statistics.eventRefund.2'),
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
      sorter: (a, b) => a.campusName?.localeCompare(b.campusName),
    },
    {
      title: t('statistics.eventRefund.3'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
    },
    {
      title: t('statistics.eventRefund.4'),
      dataIndex: 'eventName',
      key: 'eventName',
      align: 'center',
      sorter: (a, b) => a.eventName?.localeCompare(b.eventName),
    },
    {
      title: t('statistics.eventRefund.5'),
      dataIndex: 'eventDate',
      key: 'eventDate',
      align: 'center',
      sorter: (a, b) => a.eventDate?.localeCompare(b.eventDate),
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('statistics.event.1'),
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      align: 'center',
      sorter: (a, b) => a.paymentAmount - b.paymentAmount,
      render: value => value.toLocaleString(),
    },
    {
      title: t('statistics.eventRefund.6'),
      dataIndex: 'paymentMtdCode',
      key: 'paymentMtdCode',
      align: 'center',
      sorter: (a, b) => a.paymentMtdCode?.localeCompare(b.paymentMtdCode),
      render: value => getCommonCodeLabel('PA0002', value, 'ko'),
    },
    {
      title: t('statistics.eventRefund.7'),
      dataIndex: 'paymentChannelCode',
      key: 'paymentChannelCode',
      align: 'center',
      sorter: (a, b) => a.paymentChannelCode?.localeCompare(b.paymentChannelCode),
      render: value => getCommonCodeLabel('PA0004', value, 'ko'),
    },
    {
      title: t('statistics.eventRefund.8'),
      dataIndex: 'paymentDttm',
      key: 'paymentDttm',
      align: 'center',
      sorter: (a, b) => a.paymentDttm?.localeCompare(b.paymentDttm),
      render: (value: string) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: t('statistics.event.2'),
      dataIndex: 'payMemberName',
      key: 'payMemberName',
      align: 'center',
      sorter: (a, b) => a.payMemberName?.localeCompare(b.payMemberName),
    },
  ];

  return (
    <div className="content-wrapper">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <Header
        iconFile="icon-statistics-big.svg"
        parentMenu={t('common.menu.statistics')}
        currentMenu={t('statistics.event.3')}
      />

      <div className="main">
        <div className="flex flex-justify-center">
          <div className="card">
            <div className="card-body flex">
              <div className="flex">
                <div className="w-100p mr-1">
                  <SelectCampus />
                </div>
                <div className="w-100p mr-1">
                  <Select
                    className="w-100"
                    placeholder={t('statistics.eventRefund.13')}
                    value={paymentMtdCode}
                    onChange={value => setPaymentMtdCode(value)}
                  >
                    <Select.Option value="">{t('common.text.all')}</Select.Option>
                    <Select.Option value="CD">{t('statistics.eventRefund.14')}</Select.Option>
                    <Select.Option value="TF">{t('statistics.eventRefund.15')}</Select.Option>
                  </Select>
                </div>
                <div className="w-100p mr-1">
                  <Select
                    className="w-100"
                    placeholder={t('statistics.eventRefund.16')}
                    value={paymentChannelCode}
                    onChange={(value) => setPaymentChannelCode(value)}
                  >
                    <Select.Option value="">{t('common.text.all')}</Select.Option>
                    {PA0004?.map(item => (
                      <Select.Option
                        key={item.detailCode}
                        value={item.detailCode}
                      >
                        {item.korName}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="w-150p">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('statistics.eventRefund.4')}
                  value={eventName}
                  onChange={e => setEventName(e.target.value)}
                  onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
                />                
              </div>
              <div className="ml-2">
                <div className="flex">
                  <div className="date-picker__area">
                    <DatePicker
                      value={eventSrtDate ? moment(eventSrtDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setEventSrtDate('');
                        else setEventSrtDate(moment(e).format('YYYYMMDD'));
                      }}
                    />
                    <span className="pt-2 px-1">~</span>
                    <DatePicker
                      value={eventEndDate ? moment(eventEndDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setEventEndDate('');
                        else setEventEndDate(moment(e).format('YYYYMMDD'));
                      }}
                    />
                  </div>
                  <Button
                    className="btn btn-secondary btn-search ml-3 btn-w100p"
                    onClick={onSearch}
                    text={t('common.button.search')}
                  />
                </div>
                <div className="mt-2">
                  <DatePickerTable
                    setStartDate={setEventSrtDate}
                    setEndDate={setEventEndDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {res?.data?.list?.length ? (
          <>
            <div className="mt-4 flex">
              <div className="w-50">
                <DownloadButton onClick={() => { }} />
              </div>
            </div>
            <ScrollView>
              <div className="inside-table-wrapper">
                <Table
                  columns={columns}
                  dataSource={res?.data?.list}
                  pagination={{
                    pageSize: 999999999,
                  }}
                />
              </div>
            </ScrollView>
          </>
        ) : (
          <TableEmpty text={t('statistics.event.4')} />
        )}
      </div>
    </div>
  );
}

export default StatisticsEvent;
